import { useContext, useState, useEffect } from 'preact/hooks';
import { createContext, ReactNode } from 'preact/compat';
import { imageTargetStore, ImageTargetView } from '../renderer/8thwall/stores/imageTargetStore';
import { IArtwork } from '../renderer/types';

interface ImageTargetContextProps {
  imageTarget?: IArtwork;
  artworkLoading: boolean;
  imageTargetView: ImageTargetView;
  videoElement: HTMLVideoElement | null;
  audioElement: HTMLAudioElement | null;

  setImageTarget: (value?: IArtwork) => void;
  setArtworkLoading: (isLoading: boolean) => void;
  setImageTargetView: (view: ImageTargetView) => void;
  setVideoElement: (el: HTMLVideoElement | null) => void;
  setAudioElement: (el: HTMLAudioElement | null) => void;
}

const ImageTargetContext = createContext<ImageTargetContextProps | undefined>(undefined);

export const ImageTargetProvider = ({ children }: { children: ReactNode }) => {
  const [imageTarget, setImageTarget] = useState<IArtwork | undefined>(imageTargetStore.state.imageTarget);
  const [artworkLoading, setArtworkLoading] = useState<boolean>(imageTargetStore.state.artworkLoading);
  const [imageTargetView, setImageTargetView] = useState<ImageTargetView>(imageTargetStore.state.imageTargetView);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(imageTargetStore.state.videoElement);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(imageTargetStore.state.audioElement);

  useEffect(() => {
    imageTargetStore.setImageTarget = setImageTarget;
    imageTargetStore.setArtworkLoading = setArtworkLoading;
    imageTargetStore.setImageTargetView = setImageTargetView;
    imageTargetStore.setVideoElement = setVideoElement;
    imageTargetStore.setAudioElement = setAudioElement;
  }, [setImageTarget, setArtworkLoading, setImageTargetView, setVideoElement, setAudioElement]);

  useEffect(() => {
    imageTargetStore.state.imageTarget = imageTarget;
  }, [imageTarget]);

  useEffect(() => {
    imageTargetStore.state.artworkLoading = artworkLoading;
  }, [artworkLoading]);

  useEffect(() => {
    imageTargetStore.state.imageTargetView = imageTargetView;
  }, [imageTargetView]);

  useEffect(() => {
    imageTargetStore.state.videoElement = videoElement;
  }, [videoElement]);

  useEffect(() => {
    imageTargetStore.state.audioElement = audioElement;
  }, [audioElement]);

  const contextValue: ImageTargetContextProps = {
    imageTarget,
    artworkLoading,
    imageTargetView,
    videoElement,
    audioElement,
    setImageTarget,
    setArtworkLoading,
    setImageTargetView,
    setVideoElement,
    setAudioElement,
  };

  return (
    <ImageTargetContext.Provider value={contextValue}>
      {children}
    </ImageTargetContext.Provider>
  );
};

export const useImageTarget = () => {
  const context = useContext(ImageTargetContext);
  if (!context) {
    throw new Error('useImageTarget must be used within an ImageTargetProvider');
  }
  return context;
};
